import { createSignal } from 'solid-js'

declare global {
  interface Window {
    dataLayer?: unknown[]
    gtag?: (...args: unknown[]) => void
  }
}

const [bannerVisible, setBannerVisible] = createSignal(false)

export { bannerVisible }

export function openConsentBanner(): void {
  setBannerVisible(true)
}

export function closeConsentBanner(): void {
  setBannerVisible(false)
}

export type ConsentDecision = 'granted' | 'denied'

export interface StoredConsent {
  v: 1
  decision: ConsentDecision
  at: number
}

const STORAGE_KEY = 'peatch-consent'

/**
 * CNIL treats consent as time-limited rather than permanent. Six months is the
 * conservative end of its guidance (13 months being the outer bound), and it
 * applies to a refusal too: we re-ask instead of silently keeping someone denied
 * forever.
 */
const CONSENT_TTL_MS = 182 * 24 * 60 * 60 * 1000

/**
 * IANA zone prefixes that stand in for "a region where we must ask first".
 *
 * Timezone is a deliberate choice over IP geolocation: it needs no backend, no
 * third-party lookup, and no handling of the visitor's IP, so asking the
 * question costs us no personal data. It is coarse, so it is tuned to
 * over-match. `Europe/*` sweeps in non-EEA countries such as Turkey, Ukraine and
 * Russia, and that is the harmless direction to be wrong in: those visitors are
 * merely asked a question they could have skipped. Under-matching would be the
 * costly error, since it would leave an EEA visitor silently denied with no way
 * to opt in.
 */
const ASK_FIRST_ZONE_PREFIXES = [
  'Europe/',
  'Atlantic/Canary',
  'Atlantic/Madeira',
  'Atlantic/Azores',
  'Atlantic/Reykjavik',
  'Asia/Nicosia',
  'Asia/Famagusta',
  'Indian/Reunion',
  'America/Cayenne',
  'America/Martinique',
  'America/Guadeloupe',
]

export function isAskFirstRegion(): boolean {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone
    if (!zone) {
      return true
    }
    return ASK_FIRST_ZONE_PREFIXES.some((prefix) => zone.startsWith(prefix))
  } catch {
    return true
  }
}

export function readStoredConsent(): StoredConsent | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) {
      return null
    }
    const parsed = JSON.parse(raw) as Partial<StoredConsent>
    if (parsed.v !== 1 || typeof parsed.at !== 'number') {
      return null
    }
    if (parsed.decision !== 'granted' && parsed.decision !== 'denied') {
      return null
    }
    if (Date.now() - parsed.at > CONSENT_TTL_MS) {
      return null
    }
    return { v: 1, decision: parsed.decision, at: parsed.at }
  } catch {
    return null
  }
}

function persist(decision: ConsentDecision): void {
  try {
    const record: StoredConsent = { v: 1, decision, at: Date.now() }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(record))
  } catch {
    // Storage is unavailable. The decision still applies to this page view via
    // the gtag update below; we just cannot remember it for the next one.
  }
}

function pushConsentUpdate(decision: ConsentDecision): void {
  const value: ConsentDecision = decision
  const payload = {
    ad_storage: value,
    ad_user_data: value,
    ad_personalization: value,
    analytics_storage: value,
  }

  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', payload)
    return
  }

  window.dataLayer = window.dataLayer || []
  window.dataLayer.push(['consent', 'update', payload])
}

export function setConsent(decision: ConsentDecision): void {
  persist(decision)
  pushConsentUpdate(decision)
  closeConsentBanner()
}

export function initConsent(): void {
  const stored = readStoredConsent()

  if (stored) {
    if (stored.decision === 'granted') {
      pushConsentUpdate('granted')
    }
    return
  }

  if (isAskFirstRegion()) {
    openConsentBanner()
  }
}
